/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/

import { VisionApplication } from '../../application';
import { VisionLayout } from '../../layout';
import { VisionMapGeofence } from '../../map/geofence';
import { SpotRockingMap } from './map';
import { VisionRegionsWindow } from './regions.window';
import { FormElements } from '../../components/form';
import { TimeSelect } from '../../components/timeselect';
import { SpotRockingAssetData } from './spot_rocking.service';
import { Measurement } from '../../components/measurement';
import { blinkArrow, buildArrowMarkerSVG } from '../../map/helpers';

/** Stores some data about each spot rocking region.
 *  => Markers for each asset in the region so we can easily show/hide them on the map when the region is toggled in the grid.
 *  => Total rock amount for the region so we can easily update the totals in the grid when new data comes in without having to loop through all the assets again.
 */
class RegionData {
    assetData: SpotRockingAssetData[] = []; /// TODO: Not sure if this is needed or not yet.
    markers: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map<string, google.maps.marker.AdvancedMarkerElement>();
    totalRock = 0.0;

    tails: Map<string, google.maps.Polyline> = new Map<string, google.maps.Polyline>();

    /* Clears the asset data and removes all markers from the map. Called when new data is fetched to clear out old data. */
    public clear(): void {
        this.assetData = [];
        this.markers.forEach((marker) => {
            marker.map = null;
        });
        this.totalRock = 0.0;
    }
}

export class VisionSpotRockingTab {
    layout!: dhtmlXLayoutObject;
    sideLayout!: dhtmlXLayoutObject;
    toolbar!: dhtmlXToolbarObject;
    detailsToolbar!: dhtmlXToolbarObject;

    grid!: dhtmlXGridObject;
    detailsGrid!: dhtmlXGridObject;
    paramForm!: dhtmlXForm;

    map!: SpotRockingMap;
    app: VisionApplication;

    displayedRegions: Map<string, VisionMapGeofence> = new Map<string, VisionMapGeofence>();
    regionData: Map<string, RegionData> = new Map<string, RegionData>();

    constructor(vision: VisionLayout) {
        const config: IdhtmlXLayoutConfig = {
            parent: document.body,
            pattern: '3L',
        };

        this.app = VisionApplication.getInstance();
        this.layout = vision.tabbar.cells('SPOT ROCKING').attachLayout(config);
        this.sideLayout = this.layout.cells('a').attachLayout('2E');

        this.sideLayout.cells('a').setText('Regions');
        this.toolbar = this.sideLayout.cells('b').attachToolbar();
        this.toolbar.addButton('manage', 0, 'Manage Regions', 'css/imgs/new.gif', '');

        this.layout.cells('b').setText('Information');
        this.layout.cells('c').setText('Details');
        this.layout.cells('c').hideHeader();
        this.detailsToolbar = this.layout.cells('c').attachToolbar();
        this.detailsToolbar.addText('Name', 0, 'Details');
        this.detailsToolbar.addButton('export_xls', 1, 'Excel', 'icons/excel.png', '');
        this.detailsToolbar.addButton('export_pdf', 2, 'PDF', 'icons/pdf.png', '');

        this.detailsGrid = this.layout.cells('c').attachGrid();
        this.detailsGrid.setHeader('Vehicle,Date,Region,Type,Address,Amount (lbs)', '', [
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
        ]);
        this.detailsGrid.setColAlign('left,center,center,center,center,center');
        this.detailsGrid.setColTypes('ro,ro,ro,ro,ro,ro');
        this.detailsGrid.init();
        this.detailsGrid.setColumnHidden(3, true); // Hide the 'Type' column for now since we don't have that data yet.
        this.detailsGrid.setColumnHidden(4, true); // Hide the 'Address' column for now since we don't have that data yet.

        const width = window.innerWidth * 0.75;
        this.layout.cells('a').setWidth(width * 0.2);

        this.layout.setSizes();

        this.sideLayout.cells('a').hideHeader();
        this.sideLayout.cells('b').hideHeader();

        this.sideLayout.cells('a').fixSize(false, true);
        this.sideLayout.cells('a').setHeight(200);
        this.sideLayout.cells('a').setWidth(400);

        this.grid = this.sideLayout.cells('b').attachGrid();
        this.grid.setHeader('Region,Totals (tn),Display', '', ['text-align:center;', 'text-align:center;', 'text-align:center;']);
        this.grid.setImagesPath('css/imgs/');
        this.grid.setColTypes('ro,ro,ch');
        this.grid.setColAlign('left,center,center');
        this.grid.init();

        const formData = JSON.parse(JSON.stringify(TimeSelect.SHORT_FORM_NO_PERIOD));
        formData.push({
            type: 'block',
            list: [FormElements.button('search', 'Search', 'search'), { type: 'newcolumn' }, FormElements.button('cancel', 'Cancel', 'cancel')],
        });

        this.paramForm = this.sideLayout.cells('a').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';
        this.paramForm.getInput(TimeSelect.END).autocomplete = 'off';
        this.paramForm.hideItem('cancel');

        this.paramForm.setItemValue(
            TimeSelect.START,
            localStorage.getItem('spotRockingStart') ? new Date(parseInt(localStorage.getItem('spotRockingStart')!)) : new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        );
        this.paramForm.setItemValue(TimeSelect.END, localStorage.getItem('spotRockingEnd') ? new Date(parseInt(localStorage.getItem('spotRockingEnd')!)) : new Date());

        this.map = new SpotRockingMap(this);
    }

    // Called by the map once it's initialized so we know it's safe to start adding markers and regions to the map.
    afterMapInit(): void {
        this.setupEvents();
        this.refresh();
    }

    /** Sets up all the event handlers */
    private setupEvents(): void {
        this.toolbar.attachEvent('onClick', (id) => {
            switch (id) {
                case 'manage': {
                    const app = VisionApplication.getInstance();
                    new VisionRegionsWindow(app.windowManager, this);
                    break;
                }
            }
        });

        this.detailsToolbar.attachEvent('onClick', (id) => {
            switch (id) {
                case 'export_xls': {
                    this.getReport('xlsx');
                    break;
                }
                case 'export_pdf': {
                    this.getReport('pdf');
                    break;
                }
            }
        });

        this.grid.attachEvent('onRowSelect', (id) => {
            this.eventRegionSelected(id);
        });

        this.grid.attachEvent('onCheckbox', (id, _colId, state) => {
            this.showRegionOnMap(id, state);
        });

        // When a row is selected make the marker blink for a second to highlight.
        // Also move the map to cetner on that marker.
        this.detailsGrid.attachEvent('onRowSelect', (id) => {
            const regionId = this.detailsGrid.getUserData(id, 'region');
            if (regionId) {
                const marker = this.regionData.get(regionId)?.markers.get(id);
                const position = this.regionData.get(regionId)?.markers.get(id)?.position;
                if (position) {
                    this.map.map.map.setCenter(position);
                }
                if (marker) {
                    blinkArrow(marker.content as HTMLElement, 200); // 200ms blink
                }

                const polyline = this.regionData.get(regionId)?.tails.get(id);
                if (polyline) {
                    polyline.setMap(this.map.map.map);
                    setTimeout(() => {
                        polyline.setMap(null);
                    }, 2000);
                }
            }
        });

        // Search button handler to query data.
        this.paramForm.attachEvent('onButtonClick', (id) => {
            switch (id) {
                case 'search': {
                    this.getSpotRockingData();
                    break;
                }
            }
        });
    }

    /** Refresh the list of regions.  Does not refresh the data yet.
     * This is called by the regions management window after a region is added/edited/deleted to
     * update the list of regions in the grid and on the map.
     */
    public refresh(): void {
        const spotService = VisionApplication.getInstance().spotRockingService;
        spotService.fetchRegions().then((regions) => {
            this.grid.clearAll(false);
            regions.forEach((region) => {
                this.grid.addRow(region.id, [region.name, '0', '1'], 0, 0, '', false);
                this.showRegionOnMap(region.id, true);
            });
        });
    }

    private showRegionOnMap(id: string, state: boolean): void {
        const app = VisionApplication.getInstance();
        app.spotRockingService.fetchRegions().then((regions) => {
            const regionData = this.regionData.get(id);
            if (regionData) {
                regionData.markers.forEach((marker) => {
                    marker.map = state ? this.map.map.map : null;
                });

                this.detailsGrid.forEachRow((rowId) => {
                    if (this.detailsGrid.getUserData(rowId, 'region') === id) {
                        this.detailsGrid.setRowHidden(rowId, !state);
                    }
                });
            }

            const selected = regions.find((r) => r.id === id);
            if (selected) {
                let region = this.displayedRegions.get(id);
                if (state) {
                    if (!region) {
                        region = new VisionMapGeofence(this.map.map);
                        region.createGeofence(selected.geometry, '#86d3c6', 2);
                        region.polygon.setMap(this.map.map.map);
                        this.map.map.map.fitBounds(region.bounds);
                        this.displayedRegions.set(id, region);
                    } else {
                        region.polygon.setMap(this.map.map.map);
                        this.map.map.map.fitBounds(region.bounds);
                    }
                } else {
                    if (region) {
                        region.polygon.setMap(null);
                    }
                }
            }
        });
    }

    /** Called when a region is selected in the grid.
     *   Moves the map to center on that region.
     */
    private eventRegionSelected(id: string): void {
        const region = this.displayedRegions.get(id);
        if (region) {
            this.map.map.map.fitBounds(region.bounds);
        }
    }

    /** Make the query to the server to get the spot rocking data for all regions.
     *  Then updates the details grid and the markers on the map with the new data.
     */
    private getSpotRockingData(): void {
        const start = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const end = this.paramForm.getItemValue(TimeSelect.END).getTime();

        localStorage.setItem('spotRockingStart', start.toString());
        localStorage.setItem('spotRockingEnd', end.toString());

        if (!start || !end) {
            dhtmlx.message({ type: 'error', text: 'Please select both start and end times.' });
            return;
        }

        this.app.spotRockingService.fetchSpotRockingData(start, end).then((data) => {
            console.log('Fetched spot rocking data:', data);
            // Clear out the details grid and region data markers.
            this.detailsGrid.clearAll(false);
            this.regionData.forEach((region) => {
                region.clear();
            });

            data?.forEach((entry) => {
                this.addGridEntry(entry);
            });

            this.regionData.forEach((region, regionId) => {
                const rockTotalLbs = Measurement.mass(region.totalRock, false);
                this.grid.cells(regionId, 1).setValue((rockTotalLbs / 2000.0).toFixed(1));
            });
        });
    }

    private addGridEntry(entry: SpotRockingAssetData): void {
        const region = this.app.spotRockingService.getRegionById(entry.regionId);
        const regionName = region ? region.name : 'Unknown Region';
        const assetName = this.app.assetService.getAssetNameById(entry.assetId) || entry.assetId;

        // Add to the details grid.  Hide the row if the region is currently unchecked.
        const id = entry.id;
        const row_data = [
            assetName,
            new Date(entry.timestamp).toLocaleString(),
            regionName,
            '-', // Placeholder for type, would require additional logic to determine
            'N/A', // Placeholder for address, would require reverse geocoding
            Measurement.mass(entry.totalRock, false).toFixed(1),
        ];
        this.detailsGrid.addRow(id, row_data, 0, 0, '', false);
        this.detailsGrid.setUserData(id, 'region', entry.regionId);
        if (!this.grid.cells(entry.regionId, 2).isChecked()) {
            this.detailsGrid.setRowHidden(id, true);
        }

        let regionData = this.regionData.get(entry.regionId);
        if (!regionData) {
            regionData = new RegionData();
            this.regionData.set(entry.regionId, regionData);
        }
        regionData.assetData.push(entry);
        regionData.totalRock += entry.totalRock;

        // Create the marker, only show the map if the region is currently checked.
        const marker = new google.maps.marker.AdvancedMarkerElement({
            map: this.grid.cells(entry.regionId, 2).isChecked() ? this.map.map.map : null,
            position: { lat: entry.position.coordinates[1], lng: entry.position.coordinates[0] },
            content: buildArrowMarkerSVG(entry.heading, 'red'), // heading in degrees
        });
        regionData.markers.set(entry.id, marker);

        // Create the tail for the marker
        const path: google.maps.LatLng[] = []; // Initialize an empty path array

        path.push(
            new google.maps.LatLng(
                entry.position.coordinates[1], // latitude (Y)
                entry.position.coordinates[0] // longitude (X)
            )
        );
        path.push(...entry.telemetry); // Add the telemetry points to the path

        const tail = new google.maps.Polyline({
            path: path,
            icons: [
                {
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 3,
                        strokeColor: '#07da57',
                    },
                    offset: '100%',
                    repeat: '120px',
                },
            ],
            strokeColor: '#c50af3',
            strokeOpacity: 4.0,
            strokeWeight: 8,
        });
        regionData.tails.set(entry.id, tail);

        // Setup the info windowd on hover.
        const infoWindow = this.map.infoWindow;
        marker.addEventListener('mouseenter', () => {
            infoWindow.setContent(this.buildInfoContent(entry, assetName));
            infoWindow.open({
                map: this.map.map.map,
                anchor: marker,
            });
            this.detailsGrid.selectRowById(entry.id, false, true, false);
        });
        marker.addEventListener('mouseleave', () => {
            infoWindow.close();
        });
    }

    private buildInfoContent(entry: SpotRockingAssetData, name: string): string {
        const content = `
      <div style="
        font-size:14px;
        font-weight:600;
        margin-bottom:8px;
        color:#333;
      ">
        ${name}
      </div>
    
      <table style="
        border-collapse: collapse;
        font-size:13px;
        width:100%;
        min-width:220px;
      ">    
        <tr style="background:#f7f7f7;">
          <td style="padding:4px 8px;color:#666;">At</td>
          <td style="padding:4px 8px;">${new Date(entry.timestamp).toLocaleString()}</td>
        </tr>
        <tr>
          <td style="padding:4px 8px;color:#666;">Amount (lbs)</td>
          <td style="padding:4px 8px;">${Measurement.mass(entry.totalRock, false).toFixed(1)}</td>
        </tr>        
      </table>
    `;
        return content;
    }

    private getReport(format: string): void {
        const start = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const end = this.paramForm.getItemValue(TimeSelect.END).getTime();
        const selectedRegions: string[] = [];
        this.grid.forEachRow((rowId) => {
            if (this.grid.cells(rowId, 2).isChecked()) {
                selectedRegions.push(rowId);
            }
        });

        if (format == 'pdf') {
            this.app.spotRockingService.downloadPDF(
                '/spotrocking/report?selectedRegions=' + selectedRegions.join(',') + '&start=' + start + '&end=' + end + '&format=pdf',
                `spot_rocking_report_${Date.now()}.${format}`
            );
        } else if (format == 'xlsx') {
            this.app.spotRockingService.downloadXLSX(
                '/spotrocking/report?selectedRegions=' + selectedRegions.join(',') + '&start=' + start + '&end=' + end + '&format=xlsx',
                `spot_rocking_report_${Date.now()}.${format}`
            );
        }
    }
}
